import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { startMigrateConversion, getMigrateConversionStatus } from '../../../../services/wizardApi';
import { toast } from 'react-toast';
import '../../../../styles/wizard.css';

const StepM6ConvertProgress = () => {
  const navigate = useNavigate();
  const { project, migrate, setMigrate, nextStep, previousStep } = useOnboarding();
  
  const [convertJobId, setConvertJobId] = useState(null);
  const [converting, setConverting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stages, setStages] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentStageText, setCurrentStageText] = useState('Initializing...');

  // Clear old state and auto-start conversion on mount
  useEffect(() => {
    // Clear any old completion state from previous runs
    if (migrate.convertStatus === 'SUCCESS') {
      setMigrate({
        convertJobId: null,
        convertStatus: null,
        convertProgress: 0,
        stages: []
      });
    }
    
    // Auto-start if not already started
    if (!hasStarted && !convertJobId && !converting) {
      setHasStarted(true);
      handleStartConversion();
    }
  }, []); // Run only on mount

  // Poll for conversion status
  useEffect(() => {
    if (convertJobId && converting) {
      const pollInterval = setInterval(async () => {
        try {
          const response = await getMigrateConversionStatus(convertJobId);
          
          if (response.success) {
            setProgress(response.data.progress);
            const stagesList = response.data.stages || [];
            setStages(stagesList);
            
            // Update current stage text
            const inProgressStage = stagesList.find(s => s.status === 'in_progress');
            if (inProgressStage) {
              setCurrentStageText(inProgressStage.name);
            } else if (response.data.status === 'SUCCESS') {
              setCurrentStageText('All stages completed');
            }
            
            const currentStatus = response.data.status;
            setStatus(currentStatus);
            
            if (currentStatus === 'SUCCESS') {
              setConverting(false);
              clearInterval(pollInterval);
              
              // Save conversion results
              setMigrate({
                convertJobId,
                convertStatus: 'SUCCESS',
                convertProgress: 100,
                stages: response.data.stages,
                runnableSuiteId: response.data.runnable_suite_id,
                importedTestIds: response.data.imported_test_ids
              });
              
              toast.success('Conversion completed successfully!');
            } else if (currentStatus === 'FAILED') {
              setConverting(false);
              clearInterval(pollInterval);
              setError('Conversion failed. Please try again.');
              toast.error('Conversion failed');
            }
          }
        } catch (error) {
          console.error('Error polling conversion status:', error);
        }
      }, 1500);

      return () => clearInterval(pollInterval);
    }
  }, [convertJobId, converting]);

  const handleStartConversion = async () => {
    console.log('Starting conversion...');
    setConverting(true);
    setError(null);
    setProgress(0);
    setStages([]);

    try {
      const response = await startMigrateConversion({
        project_id: project.projectId,
        import_job_id: migrate.importJobId,
        settings: {
          run_mode: migrate.runMode,
          wrapper_enabled: migrate.wrapperEnabled,
          auto_deps: migrate.autoDeps,
          env_vars: migrate.envVars
        }
      });

      if (response.success) {
        const jobId = response.data.convert_job_id;
        console.log('Conversion job started:', jobId);
        setConvertJobId(jobId);
        toast.info('Starting conversion...');
      } else {
        throw new Error('Failed to start conversion');
      }
    } catch (error) {
      console.error('Error starting conversion:', error);
      setConverting(false);
      setHasStarted(false);
      setError('Failed to start conversion. Please try again.');
      toast.error('Failed to start conversion');
    }
  };

  const handleRetry = () => {
    setConvertJobId(null);
    setConverting(false);
    setProgress(0);
    setStages([]);
    setStatus(null);
    setError(null);
    setHasStarted(false);
    setMigrate({
      convertJobId: null,
      convertStatus: null,
      convertProgress: 0
    });
    // Restart conversion
    setTimeout(() => {
      setHasStarted(true);
      handleStartConversion();
    }, 100);
  };

  const handleContinue = () => {
    if (status !== 'SUCCESS') {
      toast.error('Please wait for conversion to complete');
      return;
    }

    nextStep();
    navigate('/app/onboarding?step=7');
  };

  const getStageIcon = (stageStatus) => {
    if (stageStatus === 'completed') return '✓';
    if (stageStatus === 'in_progress') return '⏳';
    return '○';
  };

  const getStageColor = (stageStatus) => {
    if (stageStatus === 'completed') return 'var(--success-color)';
    if (stageStatus === 'in_progress') return 'var(--primary-color)';
    return 'var(--text-secondary)';
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={6} completedSteps={[1, 2, 3, 4, 5]} />
        
        <WizardCard
          title="Converting scripts..."
          subtitle="Preparing your tests to run in Robitest"
        >
          {/* Compact Header */}
          <div style={{
            padding: '1rem 1.25rem',
            background: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '8px 8px 0 0',
            marginBottom: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {status === 'SUCCESS' ? (
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--success-color)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}>
                    ✓
                  </div>
                ) : (
                  <div style={{
                    width: '32px',
                    height: '32px',
                    border: '3px solid rgba(76, 201, 240, 0.3)',
                    borderTopColor: 'var(--primary-color)',
                    borderRadius: '50%',
                    animation: converting ? 'spin 1s linear infinite' : 'none'
                  }} />
                )}
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: '600' }}>
                    {status === 'SUCCESS' ? 'Conversion Complete!' : 'Converting scripts...'}
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    {status === 'SUCCESS' ? 'Your tests are ready to run' : 'Please wait...'}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: 'var(--primary-color)' }}>
                {Math.round(progress)}%
              </div>
            </div>
          </div>

          {/* Horizontal Progress Bar */}
          <div style={{
            height: '8px',
            background: 'rgba(76, 201, 240, 0.15)',
            borderRadius: '0 0 8px 8px',
            overflow: 'hidden',
            marginBottom: '0.5rem'
          }}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: status === 'SUCCESS' ? 'var(--success-color)' : 'var(--primary-color)',
              transition: 'width 0.5s ease, background-color 0.3s ease',
              boxShadow: progress > 0 ? '0 0 10px rgba(76, 201, 240, 0.5)' : 'none'
            }} />
          </div>

          {/* Current Stage Status Text */}
          <div style={{
            padding: '0.75rem 1.25rem',
            background: 'white',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '0.875rem',
              color: status === 'SUCCESS' ? 'var(--success-color)' : 'var(--primary-color)',
              fontWeight: '500'
            }}>
              {status === 'SUCCESS' ? '✓ All stages completed successfully!' : `⏳ ${currentStageText}`}
            </div>
          </div>

          {/* Compact Stages List - Always visible */}
          {stages.length > 0 && (
            <div style={{
              padding: '1.25rem',
              background: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {stages.map((stage, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.625rem 0.75rem',
                      background: 'var(--bg-gray-50)',
                      borderRadius: '4px'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
                      <span style={{
                        fontSize: '0.875rem',
                        color: getStageColor(stage.status)
                      }}>
                        {getStageIcon(stage.status)}
                      </span>
                      <span style={{
                        fontSize: '0.875rem',
                        color: stage.status === 'pending' ? 'var(--text-secondary)' : 'var(--text-primary)'
                      }}>
                        {stage.name}
                      </span>
                    </div>
                    <span style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: stage.status === 'completed' ? 'var(--success-color)' :
                             stage.status === 'in_progress' ? 'var(--primary-color)' :
                             'var(--text-secondary)',
                      minWidth: '45px',
                      textAlign: 'right'
                    }}>
                      {stage.status === 'pending' ? '—' : `${stage.progress}%`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div style={{
              padding: '2rem',
              background: 'rgba(247, 37, 133, 0.1)',
              border: '1px solid var(--danger-color)',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✗</div>
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--danger-color)' }}>
                Conversion Failed
              </h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                {error}
              </p>
              <button
                onClick={handleRetry}
                className="wizard-btn wizard-btn-primary"
              >
                🔄 Retry Conversion
              </button>
            </div>
          )}

          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=5');
            }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
            nextDisabled={status !== 'SUCCESS'}
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default StepM6ConvertProgress;